import { useCallback, useEffect, useRef, useState } from "react";
import { api, streamApi } from "../../../api/client.js";
import type { AskUserQuestion, AskUserQuestionAnswer, OnboardingMessage, OnboardingState } from "../../../api/types.js";

type OnboardingResponse = { onboarding: OnboardingState };

function liveAssistant(content = "", askUserQuestion?: AskUserQuestion): OnboardingMessage {
  return {
    id: "onboarding-live-assistant",
    role: "assistant",
    content,
    meta: askUserQuestion ? { askUserQuestion, askUserQuestionStatus: "pending" } : {}
  };
}

function isRunning(state: OnboardingState) {
  return state.run?.status === "running";
}

export function useOnboarding(initialState: OnboardingState, onStateChange?: (state: OnboardingState) => void) {
  const [state, setState] = useState(initialState);
  const [running, setRunning] = useState(() => isRunning(initialState));
  const [skipping, setSkipping] = useState(false);
  const [error, setError] = useState("");
  const [liveMessage, setLiveMessage] = useState<OnboardingMessage | null>(null);
  const [started, setStarted] = useState(() => initialState.messages.length > 0 || isRunning(initialState));
  const abortRef = useRef<AbortController | null>(null);

  const updateState = useCallback((next: OnboardingState) => {
    const nextRunning = isRunning(next);
    setState(next);
    setRunning(nextRunning);
    setStarted((current) => current || next.messages.length > 0 || nextRunning);
    onStateChange?.(next);
    return next;
  }, [onStateChange]);

  useEffect(() => {
    updateState(initialState);
  }, [initialState, updateState]);

  useEffect(() => () => abortRef.current?.abort(), []);

  const refresh = useCallback(async () => {
    const response = await api<OnboardingResponse>("/api/profile/onboarding");
    return updateState(response.onboarding);
  }, [updateState]);

  const stream = useCallback(async (body: Record<string, unknown>) => {
    if (running) return null;
    setStarted(true);
    setRunning(true);
    setError("");
    const abort = new AbortController();
    abortRef.current = abort;
    try {
      await streamApi("/api/profile/onboarding/stream", body, (event, data) => {
        if (event === "run_started") {
          setState((current) => ({ ...current, run: { id: data.runId, status: "running" } }));
        }
        if (event === "delta") {
          setLiveMessage((current) => liveAssistant(`${current?.content || ""}${data.text || ""}`, current?.meta?.askUserQuestion));
        }
        if (event === "ask_user_question") {
          setLiveMessage((current) => liveAssistant(current?.content || "", data.askUserQuestion));
        }
        if (event === "failed") setError(data.error || "引导暂时未完成，请重试。");
        if (event === "cancelled") setError("本次引导已停止。");
      }, abort.signal);
      return await refresh();
    } catch (streamError: any) {
      if (streamError?.name !== "AbortError") setError(streamError?.message || "引导暂时未完成，请重试。");
      return null;
    } finally {
      if (abortRef.current === abort) abortRef.current = null;
      setLiveMessage(null);
      setRunning(false);
    }
  }, [refresh, running]);

  const start = useCallback(() => stream({}), [stream]);
  const continueWithText = useCallback((message: string) => stream({ message: message.trim() }), [stream]);
  const submitAsk = useCallback((assistantMessageId: string, answers: AskUserQuestionAnswer[]) => (
    stream({ askResponse: { assistantMessageId, answers } })
  ), [stream]);
  const stop = useCallback(async () => {
    abortRef.current?.abort();
    await api("/api/profile/onboarding/stop", { method: "POST", body: JSON.stringify({}) }).catch(() => undefined);
    return refresh().catch(() => null);
  }, [refresh]);
  const skip = useCallback(async () => {
    if (running || skipping) return null;
    setSkipping(true);
    setError("");
    try {
      const response = await api<{ onboarding: { status: OnboardingState["status"] } }>("/api/profile/onboarding/skip", {
        method: "POST",
        body: JSON.stringify({})
      });
      return updateState({ ...state, status: response.onboarding.status });
    } catch (skipError: any) {
      setError(skipError?.message || "暂时无法跳过，请重试。");
      return null;
    } finally {
      setSkipping(false);
    }
  }, [running, skipping, state, updateState]);

  return {
    state,
    running,
    skipping,
    error,
    liveMessage,
    started,
    start,
    continueWithText,
    submitAsk,
    stop,
    skip
  };
}
